import { AppEntities } from '@/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { AppRoutes } from '@/routes';
import {
  FORMAT_DATE_DAYJS,
  FORMAT_DATE_WITH_TIME_DAYJS,
  LocalStorageKeys,
} from './constants';

dayjs.extend(localizedFormat);
dayjs.extend(utc);

/** UTC offset in hours (e.g. 8 for UTC+8, -5 for UTC-5). Matches backend UserContext.Timezone. */
export const getTimezoneOffsetHours = () =>
  String(-new Date().getTimezoneOffset() / 60);

export const isPagerVisible = (pageSize: number, itemsLength: number = 0) =>
  itemsLength > pageSize;

export const removeExtension = (filename: string) =>
  filename.replace(/\.[^/.]+$/, '');

export const getFileName = (url: string) => {
  const link = url.split('?')[0];
  const fileName = link.split('/').pop() || '';
  return fileName;
};

export const downloadFile = (href: string, fileName: string) => {
  const link = document.createElement('a');
  link.download = fileName;
  link.href = href;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const toTitleCase = (value?: string | null) => {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

export const currencyFormatted = (
  price?: number,
  options?: Intl.NumberFormatOptions,
) => {
  if (typeof price !== 'number') return null;

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    ...(options || {}),
  }).format(price);
};

export const isValidDate = (dateString: string): boolean =>
  !Number.isNaN(Date.parse(dateString));

export const localDateFormatted = (date: string) =>
  new Date(date).toLocaleDateString('en-GB');

export const localDateTimeFormatted = (
  date: string,
  options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  } as Intl.DateTimeFormatOptions,
) => new Intl.DateTimeFormat('en-GB', options).format(new Date(date));

export const localTimeFormatted = (
  date: string,
  options = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  } as Intl.DateTimeFormatOptions,
) => new Intl.DateTimeFormat('en-GB', options).format(new Date(date));

export const getDifferenceInMonthsFromDate = (from: string) => {
  const fromDate = new Date(from);

  const differenceBetweenMonths = dayjs().diff(dayjs(fromDate), 'month');

  return differenceBetweenMonths;
};

export const getOptions = <T, K extends keyof T>(
  data: T[] | undefined,
  key: K,
): Array<T[K]> => [...new Set(data?.map((item) => item[key]))];

export const getFormattedLabel = (key: string, searchValue = 'ImageFileUrl') =>
  key && key.length
    ? key
        .replace(searchValue, '')
        .split(/(?=[A-Z])/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

export const sortDataByDate = <T>(data: T[] | undefined, key: keyof T): T[] =>
  data?.sort(
    (a, b) =>
      new Date(a[key] as unknown as string).getTime() -
      new Date(b[key] as unknown as string).getTime(),
  ) || [];

export const getDataByKey = <T>(
  data: T[] | undefined,
  key: keyof T,
  inspectionStatus: number,
) =>
  data?.find((item) => (item?.[key] as unknown as number) === inspectionStatus);

function checkValueChanged(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined) {
    // Check if `b` is null, undefined, empty string, zero, false, or an empty array.
    const treatAsSame =
      b === null ||
      b === undefined ||
      b === '' ||
      b === 0 ||
      b === false ||
      (Array.isArray(b) && b.length === 0);
    return !treatAsSame;
  }

  if (b === null || b === undefined) {
    const treatAsSame =
      a === null ||
      a === undefined ||
      a === '' ||
      a === 0 ||
      a === false ||
      (Array.isArray(a) && a.length === 0);
    return !treatAsSame;
  }

  // Use deep comparison for arrays and objects.
  if (
    Array.isArray(a) ||
    typeof a === 'object' ||
    Array.isArray(b) ||
    typeof b === 'object'
  ) {
    return JSON.stringify(a) !== JSON.stringify(b);
  }

  // Direct comparison for other types.
  return a !== b;
}

export function camelize(str: string) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase(),
    )
    .replace(/\s+/g, '');
}

export function checkDataChanged<T extends object>(
  newData: T,
  oldDataStr: string | undefined,
  checkFields: string[],
): boolean {
  if (!oldDataStr || !newData || oldDataStr.length === 0) return false;

  const oldData = JSON.parse(oldDataStr);

  const hasChanged = checkFields
    .map((key) => {
      const oldValue = oldData[key];
      const newValue = (newData as any)[camelize(key)];
      const check = checkValueChanged(oldValue, newValue);
      return check;
    })
    .some((value) => value);

  return hasChanged;
}

export function sectorConverter(sector?: string): string {
  const sectorArray = sector?.split('') || [];
  return sectorArray
    .map((item, index) => (item === '1' ? index + 1 : null))
    .filter((item) => item !== null)
    .join(', ');
}

export const dateConverter = (date?: string, format?: string): string | null =>
  date ? dayjs(date).format(format || FORMAT_DATE_DAYJS) : null;

export const timeConverter = (date?: string, format?: string): string | null =>
  date
    ? dayjs
        .utc(date)
        .local()
        .format(format || FORMAT_DATE_WITH_TIME_DAYJS)
    : null;

export const groupBy = <T, K extends keyof any>(
  list: T[],
  getKey: (item: T) => K,
) =>
  list.reduce(
    (previous, currentItem) => {
      const group = getKey(currentItem);
      if (!previous[group]) previous[group] = [];
      previous[group].push(currentItem);
      return previous;
    },
    {} as Record<K, T[]>,
  );

export function baseSort<T>(a: T, b: T, field: keyof T) {
  const valA = a[field] as unknown;
  const valB = b[field] as unknown;
  // nulls or undefined first
  if (valB !== null && valA === null) return -1;
  if (valB !== undefined && valA === undefined) return -1;
  if (valA !== null && valB === null) return 1;
  if (valA !== undefined && valB === undefined) return 1;

  const valType = typeof (valA || valB);
  if (valType === 'string')
    return (valA as string).localeCompare(valB as string);

  if (valType === 'boolean')
    return ((valA as boolean) ? 1 : 0) - ((valB as boolean) ? 1 : 0);

  if (valType === 'number') return (valA as number) - (valB as number);

  return 0;
}

const isJsonValue = (value: any) =>
  Array.isArray(value) || typeof value === 'object';

export function setLocalStorageValue<T>(key: LocalStorageKeys, value: T) {
  localStorage.setItem(
    key,
    isJsonValue(value) ? JSON.stringify(value) : `${value}`,
  );
}

export function getLocalStorageValue<T>(
  key: LocalStorageKeys,
  defaultValue: T,
): T {
  const val = localStorage.getItem(key);
  if (val == null) return defaultValue;
  if (val && val.length) {
    try {
      const value = JSON.parse(val || '');
      if (isJsonValue(value)) return value as T;
    } catch {
      // ignore
    }
  }
  return val as unknown as T;
}

export function getDocumentManagementPath(
  data?: {
    modal?: string;
    entity?: AppEntities;
    entityReference?: string;
  },
  baseUrl?: AppRoutes,
) {
  const searchParams = new URLSearchParams(data);

  return `${baseUrl || AppRoutes.documentManagement}${
    searchParams ? `?${searchParams.toString()}` : ''
  }`;
}
